import { useEffect, useRef } from 'react'
import { StyleSheet, View, ViewProps } from 'react-native'
import { InsightWorkspace, useFloor, useFloors } from 'zens-store'
import { Color, OutsidePressHandler, Radius, Spacing, Table, TableCol, TableSort, Text } from 'zens-storybook'
import { isNumber } from 'lodash'
import { useTranslation } from 'react-i18next'
import { heatmapColor } from '@dms/helper'
import { useNumberParams } from 'zens-router'

import { InfoBlock } from '../info'
import { FloorplanView, FloorplanZoomView } from '../floorplan'

import InsightsHeatmapTable, { InsightsHeatmapTableRef } from './heatmaptable'
import InsightsLegendHeatmap from './legendheatmap'

const styles = StyleSheet.create({
  container: {
    flex: 1,
    paddingHorizontal: 0,
    paddingVertical: 0,
  },
  content: {
    flexDirection: 'row',
    height: '100%',
  },
  zoom: {
    width: '60%',
    aspectRatio: 3 / 2,
    minHeight: 400,
  },
  floorplan: {
    flex: 1,
    paddingTop: Spacing.S,
    paddingHorizontal: Spacing.S,
    paddingBottom: 80 + Spacing.S,
  },
  right: {
    flex: 1,
    backgroundColor: Color.sleepingGrey,
    borderRadius: Radius.M,
    borderColor: Color.calmGrey,
    borderWidth: 1,
  },
  table: {
    ...StyleSheet.absoluteFillObject,
  },
  legend: {
    position: 'absolute',
    left: 50 + Spacing.S,
    bottom: Spacing.S,
  },
  skeletonTitle: {
    padding: Spacing.M,
  },
  skeletonText: {
    backgroundColor: Color.calmGrey,
  },
  skeletonFloorplan: {
    backgroundColor: Color.simplyWhite,
    height: '100%',
  },
})

interface Props extends ViewProps {
  companyId: number
  buildingId: number
  floorId: number
  insights?: InsightWorkspace[]
  selected?: string
  onSelect?: (id?: string) => void
  sort?: TableSort
  onSortChange?: (sort: TableSort) => void
}

const InsightsHeatmapDetails = (props: Props) => {
  const { companyId, buildingId, floorId, insights, selected, onSelect, sort, onSortChange, style, ...otherProps } =
    props
  const tableRef = useRef<InsightsHeatmapTableRef>(null)
  const containerRef = useRef(null)
  const [_, setNumberParams] = useNumberParams()
  const floor = useFloor({ companyId, floorId })

  const floors = useFloors({ companyId, buildingId }, { enabled: !floorId })
  const selectedLegend = selected?.startsWith('#') ? parseInt(selected.substring(1), 10) : undefined

  function isSelected(item: InsightWorkspace) {
    if (selected?.startsWith('#')) {
      if (selected.substring(1) === 'NOSENSOR') return !isNumber(item.value)
      const index = parseInt(selected.substring(1), 10)
      return isNumber(item.value) && item.value >= index * 10 && item.value < (index + 1) * 10
    }
    return `${item.id}` === selected
  }
  const selectedItems = insights?.filter((item) => isSelected(item)) || []

  useEffect(() => {
    if (!floorId && floors.data?.length && floors.data.length > 0) {
      setNumberParams({ floorId: floors.data[0].id! })
    }
  }, [floors.data, floorId])

  useEffect(() => {
    if (selectedItems[selectedItems?.length - 1]) {
      tableRef.current?.scrollToInsight({ insight: selectedItems[selectedItems?.length - 1], viewOffset: 100 })
    }
  }, [selectedItems])

  function colorForInsight(item: InsightWorkspace) {
    const color = isNumber(item.value) ? heatmapColor(item.value) : Color.calmGrey
    return !!selected && !isSelected(item) ? Color.simplyWhite : color
  }

  function borderColorForInsight(item: InsightWorkspace) {
    return !!selected && !isSelected(item) ? Color.calmGrey : isSelected(item) ? Color.premiumBlack : undefined
  }

  function select(id: number) {
    onSelect?.(`${id}`)
  }

  function selectLegend(index: number) {
    onSelect?.(`#${isNaN(index) ? 'NOSENSOR' : index}`)
  }

  return (
    <InfoBlock style={[styles.container, style]} contentStyle={styles.content} {...otherProps}>
      <OutsidePressHandler onOutsidePress={() => selected && onSelect?.(undefined)} style={styles.zoom}>
        <FloorplanZoomView
          style={styles.floorplan}
          testID="InsightsHeatmapDetails.Floorplan"
          overlay={
            floor.data?.floorplanBackground ? (
              <InsightsLegendHeatmap style={styles.legend} onSelect={selectLegend} selected={selectedLegend} />
            ) : undefined
          }
        >
          <FloorplanView
            companyId={companyId}
            floorId={floorId}
            data={insights}
            itemColor={(item) => colorForInsight(item as InsightWorkspace)}
            itemBorderColor={(item) => borderColorForInsight(item as InsightWorkspace)}
            onItemPress={(item) => select(item.id)}
            onBackgroundPress={() => onSelect?.(undefined)}
          />
        </FloorplanZoomView>
      </OutsidePressHandler>

      <View ref={containerRef} style={styles.right}>
        <InsightsHeatmapTable
          companyId={companyId}
          buildingId={buildingId}
          floorId={floorId}
          insights={insights}
          selected={selected}
          onSelect={select}
          ref={tableRef}
          sort={sort}
          onSortChange={onSortChange}
          style={styles.table}
        />
      </View>
    </InfoBlock>
  )
}

interface SkeletonProps extends ViewProps {}
const Skeleton = (props: SkeletonProps) => {
  const { style, ...otherProps } = props
  const { t } = useTranslation()
  const cols: TableCol<InsightWorkspace>[] = [
    {
      key: 'name',
      title: t('Name'),
    },
    {
      key: 'workspaceTypeId',
      title: t('Type'),
    },
    {
      key: 'value',
      title: t('Occupancy'),
    },
  ]
  return (
    <InfoBlock.Skeleton style={[styles.container, style]} contentStyle={styles.content} {...otherProps}>
      <View style={[styles.zoom, styles.skeletonFloorplan]} />
      <View style={styles.right}>
        <Text.Skeleton
          numberOfLines={1}
          width={200}
          level={4}
          style={styles.skeletonTitle}
          contentStyle={styles.skeletonText}
        />
        <Table.Skeleton cols={cols} numberOfRows={10} />
      </View>
    </InfoBlock.Skeleton>
  )
}
InsightsHeatmapDetails.Skeleton = Skeleton

export default InsightsHeatmapDetails
